import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "sonner";

const RECITERS = [
  { value: "ar.alafasy", label: "Mishary Alafasy" },
  { value: "ar.abdulsamad", label: "Abdul Basit Abdul Samad" },
  { value: "ar.abdurrahmaansudais", label: "Abdurrahman As-Sudais" },
  { value: "ar.husary", label: "Mahmoud Al-Husary" },
  { value: "ar.mahermuaiqly", label: "Maher Al Muaiqly" }
];

const LOOP_COUNT_OPTIONS = [3, 5, 10, 20];

const buildAudioUrl = (ayahNumberInQuran, selectedReciter) =>
  `https://cdn.islamic.network/quran/audio/128/${selectedReciter}/${ayahNumberInQuran}.mp3`;

const QuranAudioContext = createContext(null);

export const useQuranAudio = () => {
  const context = useContext(QuranAudioContext);
  if (!context) {
    throw new Error("useQuranAudio must be used within QuranAudioProvider");
  }
  return context;
};

export const QuranAudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [currentSurah, setCurrentSurah] = useState(null);
  const [currentAyah, setCurrentAyah] = useState(null);
  const [ayahNumberInQuran, setAyahNumberInQuran] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [repeatMode, setRepeatMode] = useState("none"); // none | one | all
  const [reciter, setReciter] = useState("ar.alafasy");
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopCount, setLoopCount] = useState(5);
  const [loopRemaining, setLoopRemaining] = useState(0);
  const [probingReciter, setProbingReciter] = useState(false);
  const [visible, setVisible] = useState(false);
  const [audioDownloadProgress, setAudioDownloadProgress] = useState(null);

  const reciterProbeIdRef = useRef(0);
  const autoplayAllowedRef = useRef(false);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  // Media session setup
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (!currentSurah || !currentAyah) return;

    const reciterLabel = RECITERS.find((item) => item.value === reciter)?.label || "";
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: `Ayat ${currentAyah}`,
      artist: reciterLabel,
      album: currentSurah.englishName || `Surah ${currentSurah.number}`
    });

    navigator.mediaSession.setActionHandler("play", () => togglePlay());
    navigator.mediaSession.setActionHandler("pause", () => togglePlay());
    navigator.mediaSession.setActionHandler("previoustrack", playPrev);
    navigator.mediaSession.setActionHandler("nexttrack", playNext);

    return () => {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("previoustrack", null);
      navigator.mediaSession.setActionHandler("nexttrack", null);
    };
  }, [currentSurah, currentAyah, reciter]);

  const probeAudioUrl = (url) => new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    const probeAudio = new Audio();
    const cleanup = () => {
      probeAudio.removeEventListener("loadedmetadata", handleLoaded);
      probeAudio.removeEventListener("error", handleError);
      probeAudio.src = "";
    };
    const handleLoaded = () => {
      cleanup();
      resolve(true);
    };
    const handleError = () => {
      cleanup();
      resolve(false);
    };
    probeAudio.preload = "metadata";
    probeAudio.addEventListener("loadedmetadata", handleLoaded);
    probeAudio.addEventListener("error", handleError);
    probeAudio.src = url;
  });

  const safePlay = (onNotSupported) => {
    if (!audioRef.current) return;
    audioRef.current.play().catch((error) => {
      if (error?.name === "NotSupportedError") {
        if (onNotSupported) {
          onNotSupported();
        } else {
          setIsPlaying(false);
        }
        return;
      }
      if (error?.name === "NotAllowedError") {
        console.log("Autoplay prevented, waiting for user interaction");
        setIsPlaying(false);
        toast.error("Ketuk untuk memutar audio");
        return;
      }
      setIsPlaying(false);
    });
  };

  const handleAudioError = () => {
    if (!ayahNumberInQuran) return;

    autoplayAllowedRef.current = false;

    if (reciter !== "ar.alafasy") {
      setReciter("ar.alafasy");
      const fallbackSrc = buildAudioUrl(ayahNumberInQuran, "ar.alafasy");
      if (audioRef.current && fallbackSrc) {
        audioRef.current.src = fallbackSrc;
        setIsPlaying(true);
        safePlay();
      }
      toast.error("Qari tidak tersedia, diganti ke Alafasy.");
      return;
    }
    setIsPlaying(false);
    toast.error("Sumber audio tidak didukung.");
  };

  const playAyah = (surah, ayahIndex, options = {}) => {
    if (!surah || !surah.ayahs?.[ayahIndex]) return;
    if (probingReciter && !options.isAutoplay) return;

    const ayah = surah.ayahs[ayahIndex];
    const shouldResetLoop = options.resetLoop !== false;
    const audioSrc = buildAudioUrl(ayah.numberInQuran, reciter);

    if (!audioSrc) {
      console.error("No audio source for ayah", ayahIndex);
      return;
    }

    autoplayAllowedRef.current = true;

    if (audioRef.current) {
      if (audioRef.current.src !== audioSrc) {
        audioRef.current.src = audioSrc;
      }

      setCurrentSurah(surah);
      setCurrentAyah(ayahIndex + 1);
      setAyahNumberInQuran(ayah.numberInQuran);
      setIsPlaying(true);
      setAudioProgress(0);
      setVisible(true);

      safePlay(handleAudioError);

      if (loopEnabled && shouldResetLoop) {
        setLoopRemaining(loopCount);
      }
    }
  };

  const downloadSurahAudio = async (surah, selectedReciter = reciter) => {
    if (!surah?.ayahs?.length || typeof caches === "undefined") {
      return { success: false, total: 0 };
    }
    const urls = surah.ayahs
      .map((ayah) => buildAudioUrl(ayah.numberInQuran, selectedReciter))
      .filter(Boolean);
    setAudioDownloadProgress({ total: urls.length, downloaded: 0, inProgress: true });
    try {
      const cache = await caches.open("quran-audio-v1");
      let downloaded = 0;
      for (const url of urls) {
        const cached = await cache.match(url);
        if (!cached) {
          const res = await fetch(url);
          if (res.ok) {
            await cache.put(url, res.clone());
          }
        }
        downloaded += 1;
        setAudioDownloadProgress({ total: urls.length, downloaded, inProgress: true });
      }
      setAudioDownloadProgress({ total: urls.length, downloaded: urls.length, inProgress: false });
      return { success: true, total: urls.length };
    } catch (error) {
      setAudioDownloadProgress({ total: urls.length, downloaded: 0, inProgress: false });
      return { success: false, total: urls.length };
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        autoplayAllowedRef.current = false;
      } else {
        if (probingReciter) return;

        autoplayAllowedRef.current = true;
        setIsPlaying(true);
        safePlay(handleAudioError);
      }
    }
  };

  const playNext = () => {
    if (probingReciter) return;
    if (!currentSurah || !currentAyah) return;

    if (currentAyah < currentSurah.ayahs?.length) {
      playAyah(currentSurah, currentAyah, { isAutoplay: true });
    }
  };

  const playPrev = () => {
    if (probingReciter) return;
    if (!currentSurah || !currentAyah) return;

    if (currentAyah > 1) {
      playAyah(currentSurah, currentAyah - 2, { isAutoplay: true });
    }
  };

  const handleAudioEnded = () => {
    // Priority 1: Loop count feature
    if (loopEnabled) {
      if (loopRemaining > 1) {
        setLoopRemaining(prev => prev - 1);
        if (currentSurah && currentAyah) {
          playAyah(currentSurah, currentAyah - 1, { resetLoop: false, isAutoplay: true });
        }
        return;
      } else {
        setLoopRemaining(0);
      }
    }

    // Priority 2: Repeat mode
    if (repeatMode === "one") {
      if (currentSurah && currentAyah) {
        playAyah(currentSurah, currentAyah - 1, { isAutoplay: true });
      }
    } else if (repeatMode === "all") {
      if (currentSurah) {
        if (currentAyah < currentSurah.ayahs?.length) {
          playAyah(currentSurah, currentAyah, { isAutoplay: true });
        } else {
          playAyah(currentSurah, 0, { isAutoplay: true });
        }
      }
    } else {
      // No repeat - play next or stop
      if (currentSurah && currentAyah < currentSurah.ayahs?.length) {
        playAyah(currentSurah, currentAyah, { isAutoplay: true });
      } else {
        setIsPlaying(false);
      }
    }
  };

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => handleAudioEnded();
    const handleError = () => handleAudioError();
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = (e) => {
      if (audio.duration) {
        setAudioProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const handleDurationChange = (e) => {
      setAudioDuration(audio.duration);
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("durationchange", handleDurationChange);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("durationchange", handleDurationChange);
    };
  }, [currentSurah, currentAyah, loopEnabled, loopRemaining, repeatMode, reciter]);

  // Update audio source when reciter changes during playback
  useEffect(() => {
    if (!ayahNumberInQuran || !currentSurah) return;

    const isAutoplaySession = autoplayAllowedRef.current;
    if (isAutoplaySession) {
      const nextSrc = buildAudioUrl(ayahNumberInQuran, reciter);
      if (audioRef.current && audioRef.current.src !== nextSrc) {
        audioRef.current.src = nextSrc;
      }
      return;
    }

    setProbingReciter(true);
    reciterProbeIdRef.current += 1;
    const probeId = reciterProbeIdRef.current;

    const nextSrc = buildAudioUrl(ayahNumberInQuran, reciter);

    probeAudioUrl(nextSrc).then((ok) => {
      if (probeId !== reciterProbeIdRef.current) return;

      setProbingReciter(false);

      if (!ok) {
        if (reciter !== "ar.alafasy") {
          setReciter("ar.alafasy");
          toast.error("Qari tidak tersedia, diganti ke Alafasy.");
        } else {
          toast.error("Sumber audio tidak didukung.");
        }
        return;
      }

      if (audioRef.current) {
        audioRef.current.src = nextSrc;
        if (isPlaying) {
          safePlay(handleAudioError);
        }
      }
    });
  }, [reciter]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      setIsPlaying(false);
      setCurrentSurah(null);
      setCurrentAyah(null);
      setAyahNumberInQuran(null);
      setAudioProgress(0);
      setVisible(false);
    }
  };

  const value = {
    // State
    currentSurah,
    currentAyah,
    ayahNumberInQuran,
    isPlaying,
    audioProgress,
    audioDuration,
    repeatMode,
    reciter,
    loopEnabled,
    loopCount,
    loopRemaining,
    probingReciter,
    visible,
    audioDownloadProgress,
    RECITERS,
    LOOP_COUNT_OPTIONS,

    // Actions
    playAyah,
    togglePlay,
    playNext,
    playPrev,
    stopAudio,
    downloadSurahAudio,
    setRepeatMode,
    setReciter,
    setLoopEnabled,
    setLoopCount,
    setVisible,
    setCurrentSurah,
  };

  return (
    <QuranAudioContext.Provider value={value}>
      {children}
    </QuranAudioContext.Provider>
  );
};
